import * as React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePermission } from "@/hooks/userPermission";
import { PERMISSIONS } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";
import { hasPermission } from "@/utils/permission";
import { EmployerPermissions } from "@/types/user";

// Permission types that indicate teammate permission issues (not subscription issues)
const TEAMMATE_PERMISSION_TYPES = [
  PERMISSIONS.jobs,
  PERMISSIONS.chats,
  PERMISSIONS.teammates,
  PERMISSIONS.organizations,
  PERMISSIONS.verification_checks,
  PERMISSIONS.subscription,
];

export default function PermissionDialog() {
  const navigate = useNavigate();
  const { trackEvent } = usePosthog();
  const { t } = useTranslation();
  const { permitted, permissionName, closeDialog, notPermitted } = usePermission();
  const { currentPlan } = useAppSelector((state) => state.purchase);
  const { userData } = useAppSelector((state) => state.auth);

  // Keep track of the last permission name to prevent UI flicker during close animation
  const [lastPermissionName, setLastPermissionName] = React.useState(permissionName);

  React.useEffect(() => {
    if (permissionName) {
      setLastPermissionName(permissionName);
    }
  }, [permissionName]);

  // Use the last known permission name to prevent flicker when closing
  const displayPermissionName = permissionName || lastPermissionName;

  // Check if this is a teammate permission issue vs subscription issue
  const isTeammatePermission = TEAMMATE_PERMISSION_TYPES.includes(displayPermissionName);
  const isTrialExpired = displayPermissionName === PERMISSIONS.trial;
  const isSubscriptionExpired = displayPermissionName === PERMISSIONS.subscription_expired;

  const getTitle = () => {
    if (isTeammatePermission) return t("permission_dialog.permission_title");
    if (isTrialExpired) return t("permission_dialog.trial_title");
    if (isSubscriptionExpired) return t("permission_dialog.subscription_expired_title");
    return t("permission_dialog.title");
  };

  const getDescription = (name: string) => {
    if (!name) return null;
    const planName = currentPlan?.slice(0, -5) || "";
    const isStandard = planName === "go2work Standard";
    const isPremium = planName === "go2work Premium";
    const slots = isStandard ? 1 : isPremium ? 3 : 0;
    const slotLabel = isStandard ? "one" : isPremium ? "three" : "unlimited";
    const limit = isStandard ? 25 : isPremium ? 100 : 0;

    // Without a plan name, "Your {{plan}} plan has expired" would read "Your  plan".
    return t(`permission_dialog.descriptions.${name}`, {
      plan: planName,
      slots,
      slotLabel,
      limit,
    }).replace(/\s{2,}/g, " ");
  };

  const handleCloseDialog = () => {
    closeDialog();
  };

  // A §10 dialog: the title and one paragraph carry it, no hero icon, labelled actions.
  return (
    <Dialog open={!permitted} onOpenChange={handleCloseDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-1.5 text-left">
          <DialogTitle className="text-lg font-semibold tracking-tight">{getTitle()}</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
            {getDescription(displayPermissionName)}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          {isTeammatePermission ? (
            <Button
              className="rounded-md text-sm"
              onClick={() => {
                handleCloseDialog();
                trackEvent("Permission Dialog: Close Button Clicked", {
                  permission: permissionName,
                  currentPlan,
                  component: "Permission Dialog",
                });
              }}
            >
              {t("permission_dialog.buttons.close")}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="rounded-md text-sm"
                onClick={() => {
                  handleCloseDialog();
                  navigate("/employer/community");
                  trackEvent("Permission Dialog: Cancel Button Clicked", {
                    permission: permissionName,
                    currentPlan,
                    component: "Permission Dialog",
                  });
                }}
              >
                {t("permission_dialog.buttons.cancel")}
              </Button>
              <Button
                className="rounded-md text-sm"
                onClick={() => {
                  trackEvent("Permission Dialog: Upgrade Button Clicked", {
                    permission: permissionName,
                    currentPlan,
                    component: "Permission Dialog",
                  });
                  if (
                    !hasPermission(EmployerPermissions.subscription, userData?.user?.permissions)
                  ) {
                    navigate("/employer/community");
                    notPermitted({ permissionName: PERMISSIONS.subscription });
                    return;
                  }
                  closeDialog();
                  navigate("/employer/subscription");
                }}
              >
                {t("permission_dialog.buttons.see_plans")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
